import { ReactElement, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import axios from 'axios'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  activeContributorCleared,
  deleteContributor,
  fetchContributorByID,
  getContributorProfileImageUploadUrl,
  selectActiveContributor,
  selectActiveContributorStatus,
  selectDeleteContributorStatus,
  updateContributor,
} from '../../codex.slice'

let ContributorEditor = (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { contributorID } = useParams<{ contributorID: string }>()

  let contributor = useAppSelector(selectActiveContributor)
  let contributorStatus = useAppSelector(selectActiveContributorStatus)
  let deleteStatus = useAppSelector(selectDeleteContributorStatus)

  let [name, setName] = useState('')
  let [credentials, setCredentials] = useState('')
  let [title, setTitle] = useState('')
  let [bio, setBio] = useState('')
  let [hasChanges, setHasChanges] = useState(false)
  let [isSavingImage, setIsSavingImage] = useState(false)
  let [imageCacheBuster, setImageCacheBuster] = useState(Date.now())
  let [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  let fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (contributorID) {
      dispatch(fetchContributorByID(contributorID))
    }
    return () => {
      dispatch(activeContributorCleared())
    }
  }, [contributorID, dispatch])

  useEffect(() => {
    if (contributor) {
      setName(contributor.name)
      setCredentials(contributor.credentials ?? '')
      setTitle(contributor.title ?? '')
      setBio(contributor.bio ?? '')
      setHasChanges(false)
    }
  }, [contributor])

  let handleSave = () => {
    if (!contributorID) return
    dispatch(
      updateContributor({
        id: contributorID,
        data: {
          name,
          credentials: credentials || undefined,
          title: title || undefined,
          bio: bio || undefined,
        },
      }),
    )
    setHasChanges(false)
  }

  let handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (!file || !contributorID) return

    setIsSavingImage(true)
    try {
      let { url } = await getContributorProfileImageUploadUrl(contributorID)

      if (typeof url !== 'string' || !url.startsWith('https://')) return

      let parsed = new URL(url)
      if (!parsed.hostname.endsWith('.amazonaws.com')) return
      if (
        !parsed.searchParams.has('X-Amz-Signature') &&
        !parsed.searchParams.has('Signature')
      )
        return

      await axios.put(url, file, {
        headers: {
          'Cache-Control': 'max-age=2592000',
          'content-type': 'image/jpeg',
        },
      })

      setImageCacheBuster(Date.now())
      dispatch(fetchContributorByID(contributorID))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Profile image upload failed:', err)
    } finally {
      setIsSavingImage(false)
      // eslint-disable-next-line fp/no-mutation
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  let handleDelete = () => {
    if (!contributorID) return
    dispatch(deleteContributor(contributorID))
    navigate('/codex/contributors')
  }

  let isLoading = contributorStatus === 'pending'
  let isDeleting = deleteStatus === 'pending'

  let profileImageUrl = contributor?.profileImage
    ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${contributor.profileImage}?t=${imageCacheBuster}`
    : undefined

  return (
    <Container maxWidth="xl" className="py-6">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <Box>
          <Breadcrumbs sx={{ mb: 0.5 }}>
            <Link
              component="button"
              variant="body2"
              color="text.secondary"
              underline="hover"
              onClick={() => navigate('/codex/contributors')}
            >
              Contributors
            </Link>
            <Typography variant="body2" color="text.primary">
              {contributor ? 'Edit contributor' : 'Loading...'}
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {contributor?.name ?? ''}
          </Typography>
        </Box>
        <Button variant="contained" onClick={handleSave} disabled={!hasChanges}>
          Save
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {contributor && (
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
          <Box sx={{ width: 220, flexShrink: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={profileImageUrl}
                  sx={{
                    width: 96,
                    height: 96,
                    fontSize: '2rem',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {contributor.name.charAt(0).toUpperCase()}
                </Avatar>
                {isSavingImage && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      mt: '-12px',
                      ml: '-12px',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                color="primary"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Update photo
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </Box>

            {contributor.createdTime && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontWeight: 600,
                    display: 'block',
                    mb: 1,
                  }}
                >
                  Signup info
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created —{' '}
                  {dayjs(contributor.createdTime).format('D MMM YYYY')}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={
                isDeleting ? <CircularProgress size={14} /> : <DeleteIcon />
              }
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isDeleting}
              fullWidth
            >
              Delete contributor
            </Button>
          </Box>

          <Box
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setHasChanges(true)
                  }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Credentials"
                  value={credentials}
                  onChange={(e) => {
                    setCredentials(e.target.value)
                    setHasChanges(true)
                  }}
                  fullWidth
                  size="small"
                  placeholder="MD, FAEMS"
                />
              </Box>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value)
                  setHasChanges(true)
                }}
                fullWidth
                size="small"
                placeholder="Medical Director"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Bio"
                value={bio}
                onChange={(e) => {
                  setBio(e.target.value)
                  setHasChanges(true)
                }}
                fullWidth
                multiline
                minRows={4}
                size="small"
                placeholder="A short biography..."
              />
            </Paper>
          </Box>
        </Box>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete contributor?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete <strong>{contributor?.name}</strong>.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default ContributorEditor
